import type { AIModelsPromptMetadata, AIModelsPoseComposition } from '@/store/slices/aiFashionModels/types'
import {
  buildDefaultInstruction,
  buildDefaultRequirements,
  isMinorSubjectAge,
  normalizedSubjectAge,
  resolveBodyTypeDescription,
} from './aiFashionConstants'

export interface PromptOverrides {
  poseOverride?: string | null
  compositionOverride?: AIModelsPoseComposition | null
  backgroundPromptOverride?: string | null
  bodyType?: string | null
  /** Raw subject age (template `age_category`), e.g. 'baby'. Normalized/minor-checked here. */
  age?: string | null
  /** Whether the product is worn on the body. Falls back to metadata, then true. */
  isWearable?: boolean | null
}

export interface ImageIndices {
  productImageCount: number
  productImageStartIndex: number
  modelImageIndex: number | null
}

function nonEmpty(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim()
  return trimmed && trimmed.length > 0 ? trimmed : undefined
}

function resolvePose(overrides?: PromptOverrides, metadata?: AIModelsPromptMetadata | null): string | undefined {
  return nonEmpty(overrides?.compositionOverride?.pose)
    ?? nonEmpty(overrides?.poseOverride)
    ?? nonEmpty(metadata?.composition?.pose)
}

function resolveMotion(overrides?: PromptOverrides): string {
  return nonEmpty(overrides?.compositionOverride?.motion) ?? ''
}

function resolveCameraAngle(overrides?: PromptOverrides, metadata?: AIModelsPromptMetadata | null): string | undefined {
  return nonEmpty(overrides?.compositionOverride?.camera_angle)
    ?? nonEmpty(metadata?.composition?.camera_angle)
}

function resolveCrop(overrides?: PromptOverrides, metadata?: AIModelsPromptMetadata | null): string | undefined {
  return nonEmpty(overrides?.compositionOverride?.crop)
    ?? nonEmpty(metadata?.composition?.crop)
}

function resolveSubjectAge(overrides?: PromptOverrides, metadata?: AIModelsPromptMetadata | null): string | undefined {
  return normalizedSubjectAge(overrides?.age ?? metadata?.subject?.age)
}

export function validatePromptFields(
  metadata: AIModelsPromptMetadata | null,
  background: string | null | undefined,
  overrides?: PromptOverrides
): string[] {
  const missing: string[] = []
  if (!resolvePose(overrides, metadata)) missing.push('composition.pose')
  if (!resolveCameraAngle(overrides, metadata)) missing.push('composition.camera_angle')
  if (!resolveCrop(overrides, metadata)) missing.push('composition.crop')
  const resolvedBg = nonEmpty(overrides?.backgroundPromptOverride) ?? nonEmpty(background)
  if (!resolvedBg) missing.push('background')
  return missing
}

/**
 * Builds the structured AI Models prompt JSON, mirroring iOS
 * `AIModelsManager.encodeJSONPrompt`. Field order: `product?`, `instruction`,
 * `requirements`, `subject?`, `background`, `composition`.
 */
export function buildStructuredPrompt(
  metadata: AIModelsPromptMetadata,
  overrides: PromptOverrides,
  imageIndices?: ImageIndices
): string {
  const pose = resolvePose(overrides, metadata)
  const motion = resolveMotion(overrides)
  const cameraAngle = resolveCameraAngle(overrides, metadata)
  const crop = resolveCrop(overrides, metadata)
  const background = nonEmpty(overrides.backgroundPromptOverride) ?? ''

  const missing = validatePromptFields(metadata, background || null, overrides)
  if (missing.length > 0) {
    throw new Error(`Missing required prompt fields: ${missing.join(', ')}`)
  }

  const instruction = imageIndices
    ? buildDefaultInstruction(imageIndices.productImageCount, imageIndices.modelImageIndex, imageIndices.productImageStartIndex)
    : nonEmpty(metadata.instruction) ?? buildDefaultInstruction(1, 2)

  const age = resolveSubjectAge(overrides, metadata)
  const bodyType = resolveBodyTypeDescription(overrides.bodyType ?? metadata.subject?.bodyType)
  const isMinorSubject = isMinorSubjectAge(age)
  const isWearable = overrides.isWearable ?? metadata.isWearable ?? true

  const obj: Record<string, any> = {}
  const product = nonEmpty(metadata.product)
  if (product) obj.product = product
  obj.instruction = instruction
  obj.requirements = buildDefaultRequirements({ isMinorSubject, isWearable })

  const subject: Record<string, string> = {}
  if (age) subject.age = age
  if (bodyType) subject.body_type = bodyType
  if (Object.keys(subject).length > 0) obj.subject = subject

  obj.background = background
  obj.composition = { pose, motion, camera_angle: cameraAngle, crop }

  return JSON.stringify(obj)
}
